import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { IApiCompany } from 'src/app/shared/modules/graphql/types/types';
import { LoaderService } from 'src/app/shared/modules/loader/loader.service';
import { NotificationsService } from 'src/app/shared/modules/notifications/notifications.service';
import { CompanyService } from 'src/app/shared/services';

@Component({
  selector: 'app-company-merge',
  templateUrl: './company-merge.component.html',
  styleUrls: ['./company-merge.component.scss']
})
export class CompanyMergeComponent {

  public masterCompany: IApiCompany;
  public secondaryCompany: IApiCompany;
  public finalCompany: IApiCompany;
  public activeTabIndex = 1;

  constructor(
    private loader: LoaderService,
    private companyService: CompanyService,
    private notificationService: NotificationsService,
    private router: Router
  ) {

  }

  getCompanyById(id) {
    return this.loader.show$(this.companyService.getByIdForMerge(id));
  }


  private setCompany(event: any, type: 'master' | 'secondary'): void {
    this.finalCompany = null;

    const companyKey = type === 'master' ? 'masterCompany' : 'secondaryCompany';
    this[companyKey] = event;

    if (event?.id) {
      this.getCompanyById(event.id).subscribe(response => {
        response.Branches = response?.Branches?.sort((a, b) => a?.name?.localeCompare(b?.name));
        response?.Branches?.forEach(branch => {
          branch.ContactAssignments = branch.ContactAssignments.filter(
            assignment => assignment.Contact?.id
          )
        });
        this[companyKey] = response;
        this.generateFinalCompanyData();
      });
    }
  }

  public setMasterCompany(event: any): void {
    this.setCompany(event, 'master');
  }

  public setSecondaryCompany(event: any): void {
    this.setCompany(event, 'secondary');
  }

  public reload(type): void {
    this.setCompany(type === 'master' ? this.masterCompany : this.secondaryCompany, type);
  }

  public reloadPage(){
    this.reload('master');
    this.reload('secondary')
  }

  public generateFinalCompanyData() {
    if (this.masterCompany?.id && this.secondaryCompany?.id) {
      let billingRules = [];
      if (this.masterCompany?.BillingRules?.length || this.secondaryCompany?.BillingRules?.length) {
        billingRules = this.masterCompany?.BillingRules?.length ? [...this.masterCompany?.BillingRules] : [];
        this.secondaryCompany?.BillingRules?.forEach(rule => {
          const isExpense = !!rule.ExpenseItem;
          const ruleId = isExpense ? rule.ExpenseItem?.id : rule.TimeEntryType?.id;
          const existsInMaster = this.masterCompany?.BillingRules.some(r =>
            isExpense
              ? r.ExpenseItem?.id === ruleId
              : r.TimeEntryType?.id === ruleId
          );
          if (!existsInMaster) {
            billingRules.push(rule);
          }
        });
      }

      let flatRates = [];
      if (this.masterCompany?.FlatRates?.length || this.secondaryCompany?.FlatRates?.length) {
        flatRates = this.masterCompany?.FlatRates?.length ? [...this.masterCompany?.FlatRates] : [];
        this.secondaryCompany?.FlatRates?.forEach(rate => {
          const exist = this.masterCompany?.FlatRates?.some(v => v.Risk?.id === rate?.Risk?.id);
          if (!exist) flatRates.push(rate);
        })
      }

      let excludedStaff = [];
      if (this.masterCompany?.ExcludedStaff?.length || this.secondaryCompany?.ExcludedStaff?.length) {
        excludedStaff = this.masterCompany?.ExcludedStaff?.length ? [...this.masterCompany?.ExcludedStaff] : [];
        this.secondaryCompany?.ExcludedStaff?.forEach(staff => {
          const exist = this.masterCompany?.ExcludedStaff?.some(v => v.id === staff.id);
          if (!exist) excludedStaff.push(staff);
        });
      }

      let requiredServices = [];
      if (this.masterCompany?.RequiredServices?.length || this.secondaryCompany?.RequiredServices?.length) {
        requiredServices = this.masterCompany?.RequiredServices?.length ? [...this.masterCompany?.RequiredServices] : [];
        this.secondaryCompany?.RequiredServices?.forEach(service => {
          const exist = this.masterCompany?.RequiredServices?.some(v => v.id === service.id);
          if (!exist) requiredServices.push(service);
        });
      }

      let requiredCertifications = [];
      if (this.masterCompany?.RequiredCertifications?.length || this.secondaryCompany?.RequiredCertifications?.length) {
        requiredCertifications = this.masterCompany?.RequiredCertifications?.length ? [...this.masterCompany?.RequiredCertifications] : [];
        this.secondaryCompany?.RequiredCertifications?.forEach(certification => {
          const exist = this.masterCompany?.RequiredCertifications?.some(v => v.id === certification.id);
          if (!exist) requiredCertifications.push(certification);
        });
      }

      let mergedBranches = [];
      if (this.masterCompany?.Branches?.length || this.secondaryCompany?.Branches?.length) {
        mergedBranches = this.masterCompany?.Branches?.length ? JSON.parse(JSON.stringify(this.masterCompany.Branches)) : [];
        this.secondaryCompany?.Branches?.forEach(secBranch => {
          const masterBranchIndex = mergedBranches.findIndex(
            masterBranch => masterBranch?.name?.toLowerCase()?.trim() === secBranch?.name?.toLowerCase()?.trim()
          );

          if (masterBranchIndex !== -1) {
            const masterBranch = mergedBranches[masterBranchIndex];
            secBranch?.Addresses?.forEach(secAddr => {
              const exists = masterBranch?.Addresses?.some(masterAddr =>
                ['address1', 'address2', 'city', 'state', 'postal', 'country'].every(
                  key => masterAddr[key]?.toLowerCase()?.trim() === secAddr[key]?.toLowerCase()?.trim()
                )
              );
              if (!exists) {
                masterBranch.Addresses.push(secAddr);
              }
            });

            // Merge Phones
            secBranch?.Phones?.forEach(secPhone => {
              const exists = masterBranch?.Phones?.some(masterPhone =>
                masterPhone?.number?.toLowerCase()?.trim() === secPhone?.number?.toLowerCase()?.trim()
              );
              if (!exists) {
                masterBranch.Phones.push(secPhone);
              }
            });

            // Merge Emails
            secBranch?.Emails?.forEach(secEmail => {
              const exists = masterBranch?.Emails?.some(masterEmail =>
                masterEmail?.address?.toLowerCase()?.trim() === secEmail?.address?.toLowerCase()?.trim()
              );
              if (!exists) {
                masterBranch.Emails.push(secEmail);
              }
            });

            // Merge ContactAssignments ignore duplicates
            if (secBranch?.ContactAssignments?.length) {
              const existingContactIds = new Set(
                masterBranch.ContactAssignments.map(ca => ca?.Contact?.id)
              );
              const nonDuplicateAssignments = secBranch?.ContactAssignments?.filter(
                ca => !existingContactIds.has(ca?.Contact?.id)
              );
              masterBranch.ContactAssignments.push(...nonDuplicateAssignments);
            }
            if (secBranch?.BillToBranchInvestigation?.length)
              masterBranch.BillToBranchInvestigation.push(...secBranch.BillToBranchInvestigation);
            if (secBranch?.ClientBranchInvestigation?.length)
              masterBranch.ClientBranchInvestigation.push(...secBranch.ClientBranchInvestigation);
          } else {
            // Add new branch from secondary if not found in master
            mergedBranches?.push(secBranch);
          }
        });
      }
      const investigations = [...(this.masterCompany?.Investigations?.length ? this.masterCompany?.Investigations : []), ...(this.secondaryCompany?.Investigations?.length ? this.secondaryCompany?.Investigations : [])]
      const info: IApiCompany = {
        id: this.masterCompany?.id,
        name: this.masterCompany.name,
        qbListId: this.masterCompany.qbListId || this.secondaryCompany.qbListId,
        recordedInterviewNotification: this.masterCompany.recordedInterviewNotification,
        enterPropertyEnabled: this.masterCompany.recordedInterviewNotification,
        description: this.masterCompany.description || this.secondaryCompany.description,
        billingInstructions: this.masterCompany?.billingInstructions || this.secondaryCompany?.billingInstructions,
        additionalInfo: this.masterCompany?.additionalInfo || this.secondaryCompany?.additionalInfo,
        Type: this.masterCompany.Type,
        BillingRules: billingRules,
        FlatRates: flatRates,
        ExcludedStaff: excludedStaff,
        RequiredServices: requiredServices,
        RequiredCertifications: requiredCertifications,
        Branches: mergedBranches,
        Investigations: investigations
      }
      this.finalCompany = info;
    } else {
      this.finalCompany = null;
    }
  }

  mergeCompany() {
    if (this.masterCompany?.id === this.secondaryCompany?.id) {
      this.notificationService.alert("Master and Secondary company should be different", "Merge Company Error");
      return;
    }
    this.notificationService
      .kendoConfirm(
        `Are you sure you want to merge ${this.secondaryCompany.name} into ${this.masterCompany.name}? All data will be transferred to ${this.masterCompany.name}, and ${this.secondaryCompany.name} will be permanently deleted. This action cannot be undone.`,
        "Merge Company?",
        "No, Don’t Merge",
        "Yes, Merge",
        550
      ).pipe(filter(v => !!v))
      .subscribe(() => {
        this.loader.show$(this.companyService.mergeCompanies({
          masterCompanyId: this.masterCompany.id,
          secondaryCompanyId: this.secondaryCompany.id
        })).subscribe(res => {
          if (res === true) {
            this.notificationService.notify(`${this.secondaryCompany.name} is merge into ${this.masterCompany.name} successfully`);
            this.router.navigate([`/admin/companies/management/${this.masterCompany.id}`]);
          } else {
            this.notificationService.error("Error merging companies, please try again later");
          }
        }, error => {
          this.notificationService.error("Error merging companies, please try again later");
        });
      });
  }
}
